import React from 'react';
import { useIntl } from 'react-intl';

import { OrderPunctualityType, PunctualityInsight } from 'apis/merchant-gateway/insights';
import { InsightResponseWithPreviousInterval, MetricRow, RowType } from 'modules/insights-v2/types';
import { formatGrowthRate, getGrowthRateForFields, getGrowthTrend } from 'modules/insights-v2/utils';

import { DEFAULT_PUNCTUALITY } from './constants';
import SummaryTable from '../InsightSummary/SummaryTable';

type PunctualityTableProps = {
  punctualityInsight: InsightResponseWithPreviousInterval<PunctualityInsight>;
  title: string;
  tooltip: string;
};
const PunctualityTable = ({ punctualityInsight, title, tooltip }: PunctualityTableProps) => {
  const punctualityTotalForSelected =
    punctualityInsight?.selected?.data?.data?.punctuality?.total ?? DEFAULT_PUNCTUALITY;
  const punctualityTotalForPrevious =
    punctualityInsight?.previous?.data?.data?.punctuality?.total ?? DEFAULT_PUNCTUALITY;

  const { locale } = useIntl();
  const {
    earlyOrders: earlyOrdersGrowth,
    lateOrders: lateOrdersGrowth,
    onTimeOrders: onTimeOrdersGrowth,
  } = getGrowthRateForFields(punctualityTotalForSelected, punctualityTotalForPrevious, [
    OrderPunctualityType.EARLY,
    OrderPunctualityType.LATE,
    OrderPunctualityType.ON_TIME,
  ]);

  const rows: MetricRow[] = [
    {
      type: RowType.Metric,
      title: 'insights.punctuality.orders-early',
      metrics: punctualityTotalForSelected.earlyOrders,
      growthRate: formatGrowthRate(earlyOrdersGrowth, locale),
      growthTrend: getGrowthTrend(earlyOrdersGrowth),
    },
    {
      type: RowType.Metric,
      title: 'insights.punctuality.orders-on-time',
      metrics: punctualityTotalForSelected.onTimeOrders,
      growthRate: formatGrowthRate(onTimeOrdersGrowth, locale),
      growthTrend: getGrowthTrend(onTimeOrdersGrowth),
    },
    {
      type: RowType.Metric,
      title: 'insights.punctuality.orders-late',
      metrics: punctualityTotalForSelected.lateOrders,
      growthRate: formatGrowthRate(lateOrdersGrowth, locale),
      growthTrend: getGrowthTrend(lateOrdersGrowth, true),
    },
  ];
  return (
    <SummaryTable
      isFetching={punctualityInsight.isFetching}
      isError={punctualityInsight.isError}
      onRetry={punctualityInsight.refetch}
      title={title}
      tooltip={tooltip}
      rows={rows}
    />
  );
};

export default PunctualityTable;
